import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemQualifyingGroup from "Reporting/Components/ReportItemQualifyingGroup";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { render } from "@testing-library/react";
import { Select } from "shared/Select/Select";

/*
 * Table displaying different items in a report with the functionality to sort
 */
export default function ReportTableQualifyingGroup({
  reports,
  filters,
  groupName,
  onSortedReportsChange,
}) {
  const PAGE_HEIGHT_PX = 475;
  const tableRef = useRef(null);

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([{ field: "Last Name", direction: "asc" }]);

  const getAllOptions = (field) => {
    if (field === "pin") {
      return Array.from(new Set(reports.map((r) => r.pin).filter(Boolean)));
    } else if (field === "hpin") {
      return Array.from(new Set(reports.map((r) => r.hpin).filter(Boolean)));
    } else if (field === "diamond") {
      return Array.from(new Set(reports.map((r) => r.diamond).filter(Boolean)));
    } else {
      return Array.from(
        new Set(
          reports
            .flatMap((r) =>
              r.columns.filter((c) => c.name === field).map((c) => c.value)
            )
            .filter(Boolean)
        )
      );
    }
  };

  const [selectedFilters, setSelectedFilters] = useState(() => {
    // By default, select all for pin and diamond
    const initial = {};
    const pinOptions = getAllOptions("pin");
    if (pinOptions.length > 0) initial.pin = [...pinOptions];
    const hpinOptions = getAllOptions("hpin");
    if (hpinOptions.length > 0) initial.hpin = [...hpinOptions];
    const diamondOptions = getAllOptions("diamond");
    if (diamondOptions.length > 0) initial.diamond = [...diamondOptions];
    return initial;
  });

  useEffect(() => {
    setSelectedFilters((prev) => {
      const next = { ...prev };

      const pinOptions = getAllOptions("pin");
      if (pinOptions.length > 0) {
        const prevPins = Array.isArray(prev.pin) ? prev.pin : [];
        const kept = prevPins.filter((p) => pinOptions.includes(p));
        const added = pinOptions.filter((p) => !prevPins.includes(p));
        next.pin = [...kept, ...added];
      } else {
        delete next.pin;
      }

      const hpinOptions = getAllOptions("hpin");
      if (hpinOptions.length > 0) {
        const prevHpins = Array.isArray(prev.hpin) ? prev.hpin : [];
        const keptH = prevHpins.filter((p) => hpinOptions.includes(p));
        const addedH = hpinOptions.filter((p) => !prevHpins.includes(p));
        next.hpin = [...keptH, ...addedH];
      } else {
        delete next.hpin;
      }

      const diamondOptions = getAllOptions("diamond");
      if (diamondOptions.length > 0) {
        const prevDiamonds = Array.isArray(prev.diamond) ? prev.diamond : [];
        const keptD = prevDiamonds.filter((d) => diamondOptions.includes(d));
        const addedD = diamondOptions.filter((d) => !prevDiamonds.includes(d));
        next.diamond = [...keptD, ...addedD];
      } else {
        delete next.diamond;
      }

      const isEqual = JSON.stringify(prev) === JSON.stringify(next);

      return isEqual ? prev : next;
    });
  }, [reports]);

  const [sortedReports, setSortedReports] = useState(reports);
  const [totalItem, setTotalItem] = useState({
    IBO: "Total",
    lastName: "",
    displayName: "",
    pin: "",
    hpin: "",
    email: "",
    topicsId: "",
    diamond: "",
    users: 0,
    columns: [],
  });

  const allColumns = useMemo(() => {
    const seen = new Set();
    const grouped = [];

    reports.forEach((report, reportIndex) => {
      const cols = report.columns
        .filter((col) => {
          if (seen.has(col.name)) return false;
          seen.add(col.name);
          return true;
        })
        .sort((a, b) => a.sort - b.sort);

      grouped.push(...cols.map((c) => ({ ...c, reportIndex })));
    });

    // Now sort by report index first (group order), then sort number
    return grouped.sort((a, b) => {
      if (a.reportIndex !== b.reportIndex) {
        return a.reportIndex - b.reportIndex;
      }
      return a.sort - b.sort;
    });
  }, [reports]);

  useEffect(() => {
    if (!sortedReports || sortedReports.length === 0) {
      setTotalItem(null);
      return;
    }

    const newTotal = {
      IBO: "Total",
      lastName: "",
      displayName: "",
      pin: "",
      hpin: "",
      email: "",
      topicsId: "",
      diamond: "",
      users: sortedReports.reduce((sum, r) => {
        const val = Number(r.users);
        return sum + (isNaN(val) ? 0 : val);
      }, 0),
      columns: allColumns.map((col) => {
        // Only sum columns with integer sort
        if (!Number.isInteger(col.sort)) return { ...col, value: "" };

        const total = sortedReports.reduce((sum, r) => {
          const colVal = r.columns.find((c) => c.name === col.name)?.value;
          const numVal = Number(colVal);
          return sum + (isNaN(numVal) ? 0 : numVal);
        }, 0);

        return { ...col, value: total };
      }),
    };

    setTotalItem(newTotal);
  }, [sortedReports, allColumns]);

  useEffect(() => {
    let result = [...reports];

    const hasEmptyFilter = Object.values(selectedFilters).some(
      (values) => Array.isArray(values) && values.length === 0
    );
    if (hasEmptyFilter) {
      setSortedReports([]);
      return;
    }

    // apply selectedFilters (filtering)
    Object.entries(selectedFilters).forEach(([field, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((r) => {
        let compareVal;
        switch (field) {
          case "pin":
            compareVal = r.pin;
            break;
          case "hpin":
            compareVal = r.hpin;
            break;
          case "diamond":
            compareVal = r.diamond;
            break;
          default:
            compareVal = r.columns.find((c) => c.name === field)?.value || "";
        }
        return values.includes(compareVal);
      });
    });

    // apply sorting (multi-sort)
    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const s of sort) {
          if (!s.field || s.direction === "none") continue;

          let aVal, bVal;
          switch (s.field) {
            case "IBO":
              aVal = a.IBO;
              bVal = b.IBO;
              break;
            case "Last Name":
              aVal = a.lastName;
              bVal = b.lastName;
              break;
            case "PIN":
              aVal = a.pin;
              bVal = b.pin;
              break;
            case "Highest PIN":
              aVal = a.hpin;
              bVal = b.hpin;
              break;
            case "Diamond":
              aVal = a.diamond;
              bVal = b.diamond;
              break;
            default:
              aVal = a.columns.find((col) => col.name === s.field)?.value || 0;
              bVal = b.columns.find((col) => col.name === s.field)?.value || 0;
          }

          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if (aVal < bVal) return s.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return s.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedReports(result);
  }, [reports, sort, selectedFilters]);

  // Notify parent component whenever sorted/filtered data changes
  useEffect(() => {
    if (onSortedReportsChange && sortedReports) {
      onSortedReportsChange(groupName, sortedReports);
    }
  }, [sortedReports, groupName, onSortedReportsChange]);

  const updateFilter = (field, value) => {
    setSelectedFilters((prev) => {
      const current = prev[field] || [];
      const allOptions = getAllOptions(field);

      let newValues;
      if (value === "All") {
        // Toggle all: if all selected, clear; else select all
        if (current.length === allOptions.length) {
          newValues = [];
        } else {
          newValues = [...allOptions];
        }
      } else {
        // Toggle individual value
        if (current.includes(value)) {
          newValues = current.filter((v) => v !== value);
        } else {
          newValues = [...current, value];
        }
        // If after toggling, all are selected, treat as all selected
        if (newValues.length === allOptions.length) {
          newValues = [...allOptions];
        }
      }
      // Always remove any accidental 'All' string from the array
      newValues = newValues.filter((v) => v !== "All");
      return { ...prev, [field]: newValues };
    });
  };

  const toggleSort = (field) => {
    setSort((prev) => {
      const existingIndex = prev.findIndex((s) => s.field === field);
      let newSort;

      if (existingIndex === -1) {
        newSort = [...prev, { field, direction: "asc" }];
      } else {
        const existing = prev[existingIndex];
        let nextDirection;
        if (existing.direction === "asc") nextDirection = "desc";
        else if (existing.direction === "desc") nextDirection = "none";
        else nextDirection = "asc";

        if (nextDirection === "none") {
          newSort = prev.filter((_, i) => i !== existingIndex);
        } else {
          newSort = [...prev];
          newSort[existingIndex] = { field, direction: nextDirection };
        }
      }

      return newSort;
    });
  };

  const renderSortIcon = (field) => {
    const index = sort.findIndex((s) => s.field === field);
    if (index === -1 || sort[index].direction === "none") {
      return <FontAwesomeIcon icon={faMinus} className={styles.menuChevron} />;
    }

    const icon =
      sort[index].direction === "asc"
        ? faChevronUp
        : sort[index].direction === "desc"
        ? faChevronDown
        : faMinus;

    return (
      <div className={styles.sortWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.menuChevron} />
        <span className={styles.sortPriority}>{index + 1}</span>
      </div>
    );
  };

  // Calculate page breaks for PDF/export, using a single ref
  const calculateBreaks = (ref, rowCount, setBreaks) => {
    if (!ref.current) {
      console.warn("Ref not available for break calculation");
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      // Get both the header and the title (headerInfo) heights
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      // headerInfo is outside the tableRef, so get it from the parent
      let headerInfoHeight = 0;
      const parent = ref.current.parentElement;
      if (parent) {
        const headerInfoEl = parent.querySelector(`.${styles.headerInfo}`);
        if (headerInfoEl)
          headerInfoHeight = headerInfoEl.getBoundingClientRect().height;
      }

      let staticHeight = headerInfoHeight + headerHeight;
      // If headerInfoHeight is 0, manually add 50px for the title/header
      if (headerInfoHeight === 0) {
        staticHeight += 50;
        // console.log(
        //   "[PageBreak] headerInfoHeight missing, adding 50px manually"
        // );
      }
      // console.log(
      //   "[PageBreak] headerHeight:",
      //   headerHeight,
      //   "headerInfoHeight:",
      //   headerInfoHeight,
      //   "staticHeight:",
      //   staticHeight
      // );

      let heightSoFar = 0;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        // console.log(`[PageBreak] Row ${i} height:`, rect.height);
        if (isFirstPage) {
          heightSoFar += staticHeight;
          isFirstPage = false;
        }
        // Check if adding this row would exceed the page height
        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          // console.log(
          //   `[PageBreak] --- PAGE BREAK at row ${currentRowIndex}, resetting heightSoFar to row height:`,
          //   rect.height
          // );
          heightSoFar = rect.height;
        } else {
          heightSoFar += rect.height;
        }
        // console.log(`[PageBreak] After row ${i}, heightSoFar:`, heightSoFar);
        currentRowIndex++;
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(
      tableRef,
      sortedReports.length + (totalItem ? 1 : 0),
      setPageBreakIndexes
    );
  }, [sortedReports, totalItem]);

  return (
    <div>
      {/* {index !== 0 && <div className={styles.pageBreak} />} */}
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>
            {(() => {
              const validNames = (filters.eventNames || []).filter(
                (name) => name && name !== "All"
              );

              if (validNames.length === 0) return null;

              return `${validNames.join(" & ")} - `;
            })()}
            {groupName}
          </div>

          {/* <span className={styles.row}>
            <>Year:</> <div className={styles.bold}>{filters.year}</div>
          </span>
          <span className={styles.row}>
            <>Event Type:</> <div className={styles.bold}>{filters.type}</div>
          </span>
          {filters.eventNames.filter((name) => name).length > 0 && (
            <span className={styles.row}>
              <>Event{filters.eventNames.length > 1 ? "s" : ""}:</>
              <div className={styles.bold}>
                {filters.eventNames.map((name) => name && name).join(", ")}
              </div>
            </span>
          )}
          <span className={styles.row}>
            <>Qualified Report:</>
            <div className={styles.bold}>{groupName}</div>
          </span> */}
        </div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.header}>
            {/* compute scaled widths: pivot columns get 5% each, remaining percentage is distributed
                across the fixed columns proportionally based on their base widths */}
            {(() => {
              const fixedBase = {
                ibo: 6,
                lastName: 8,
                displayName: 13,
                pin: 6,
                hpin: 10,
                email: 15,
                topicsId: 5,
                diamond: 8,
                users: 5,
              };
              const baseSum = Object.values(fixedBase).reduce(
                (a, b) => a + b,
                0
              );
              const pivotPer = 5; // percent per pivot column
              const pivotCount = allColumns.length;
              const pivotTotal = pivotPer * pivotCount;
              const remainingPct = Math.max(100 - pivotTotal, 0);
              const scaledWidths = Object.fromEntries(
                Object.entries(fixedBase).map(([k, v]) => [
                  k,
                  (v / baseSum) * remainingPct,
                ])
              );

              return (
                <>
                  <div
                    className={styles.ibo}
                    onClick={() => toggleSort("IBO")}
                    style={{ width: `${scaledWidths.ibo}%` }}
                  >
                    IBO{renderSortIcon("IBO")}
                  </div>
                  <div
                    className={styles.lastName}
                    onClick={() => toggleSort("Last Name")}
                    style={{ width: `${scaledWidths.lastName}%` }}
                  >
                    Last Name{renderSortIcon("Last Name")}
                  </div>
                  <div
                    className={styles.displayName}
                    style={{ width: `${scaledWidths.displayName}%` }}
                  >
                    IBOs
                  </div>
                  <div
                    className={styles.pin}
                    style={{ width: `${scaledWidths.pin}%` }}
                  >
                    <Select
                      options={[
                        "All",
                        ...new Set(reports.map((r) => r.pin).filter(Boolean)),
                      ].map((pin) => pin)}
                      placeholder={
                        !selectedFilters.pin || selectedFilters.pin.length === 0
                          ? "PIN"
                          : selectedFilters.pin.length ===
                            new Set(reports.map((r) => r.pin).filter(Boolean))
                              .size
                          ? "PIN"
                          : selectedFilters.pin.join(", ")
                      }
                      onChange={(value) => updateFilter("pin", value)}
                      selectedValues={selectedFilters.pin || []} // <-- NEW
                    />
                    <div onClick={() => toggleSort("PIN")}>
                      {renderSortIcon("PIN")}
                    </div>
                  </div>
                  <div
                    className={styles.hpin}
                    style={{ width: `${scaledWidths.hpin}%` }}
                  >
                    <Select
                      className={styles.hpinSelect}
                      options={["All", ...getAllOptions("hpin")]}
                      placeholder={
                        !selectedFilters.hpin ||
                        selectedFilters.hpin.length === 0 ||
                        selectedFilters.hpin.length ===
                          getAllOptions("hpin").length
                          ? "Highest PIN"
                          : selectedFilters.hpin.join(", ")
                      }
                      onChange={(value) => updateFilter("hpin", value)}
                      selectedValues={selectedFilters.hpin || []}
                    />
                    <div onClick={() => toggleSort("Highest PIN")}>
                      {renderSortIcon("Highest PIN")}
                    </div>
                  </div>
                  <div
                    className={styles.email}
                    style={{ width: `${scaledWidths.email}%` }}
                  >
                    Email
                  </div>
                  <div
                    className={styles.topicsId}
                    style={{ width: `${scaledWidths.topicsId}%` }}
                  >
                    Topic ID
                  </div>
                  <div
                    className={styles.diamond}
                    style={{ width: `${scaledWidths.diamond}%` }}
                  >
                    <Select
                      options={[
                        "All",
                        ...new Set(
                          reports.map((r) => r.diamond).filter(Boolean)
                        ),
                      ].map((diamond) => diamond)}
                      placeholder={
                        !selectedFilters.diamond ||
                        selectedFilters.diamond.length === 0
                          ? "Diamond"
                          : selectedFilters.diamond.length ===
                            new Set(
                              reports.map((r) => r.diamond).filter(Boolean)
                            ).size
                          ? "Diamond"
                          : selectedFilters.diamond.join(", ")
                      }
                      onChange={(value) => updateFilter("diamond", value)}
                      selectedValues={selectedFilters.diamond || []}
                    />
                    <div onClick={() => toggleSort("Diamond")}>
                      {renderSortIcon("Diamond")}
                    </div>
                  </div>
                  <div
                    className={styles.users}
                    style={{ width: `${scaledWidths.users}%` }}
                  >
                    Users
                  </div>
                  {allColumns.map((col, index, arr) => {
                    // pivot columns each get pivotPer percent
                    const colWidth = pivotPer;

                    return (
                      <div
                        key={col.name}
                        className={index % 2 !== 0 ? styles.col : styles.oddCol}
                        style={{ width: `${colWidth}%` }}
                        // onClick={() => toggleSort(col.name)}
                      >
                        {col.name}
                        {/* {renderSortIcon(col.name)} */}
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>

          {totalItem && (
            <React.Fragment key="total">
              {pageBreakIndexes.includes(sortedReports.length) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemQualifyingGroup
                  item={
                    totalItem.IBO
                      ? totalItem
                      : {
                          IBO: "Total",
                          lastName: "",
                          displayName: "",
                          pin: "",
                          hpin: "",
                          email: "",
                          topicsId: "",
                          diamond: "",
                          users: 0,
                          columns: [],
                        }
                  }
                  isOdd={sortedReports.length % 2 !== 0}
                  type="total"
                  allColumns={allColumns}
                />
              </div>
            </React.Fragment>
          )}

          {sortedReports.map((row, i) => (
            <React.Fragment key={i}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemQualifyingGroup
                  item={
                    row.IBO
                      ? row
                      : {
                          IBO: "",
                          lastName: "",
                          displayName: "",
                          pin: "",
                          hpin: "",
                          email: "",
                          topicsId: "",
                          diamond: "",
                          users: 0,
                          columns: [],
                        }
                  }
                  isOdd={i % 2 !== 0}
                  type="row"
                  allColumns={allColumns}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableQualifyingGroup.propTypes = {
  item: PropTypes.shape({
    eventOwner: PropTypes.string,
    eventType: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    events: PropTypes.arrayOf(PropTypes.object),
  }),
};
